import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { ZorroTableComponent } from '../custom-table/zorro-table.component';
import { MenuDropdownComponent } from '../menu-dropdown/menu-dropdown.component';

export interface AccordionSection {
  id: string | number;
  title: string;
  itemCount: number;
  testSuiteName?: string;
  testPlanName?: string;
  testPlanId?: any;
  createdAt?: string;
  testcaseCount?: any;
  testSuiteId?: any;
  data?: any;
  expanded?: boolean;
  loading?: boolean;
  tableColumns?: any[];
  tableRows?: any[];
  tableOptions?: any;
  typeId?: string;
  name?: string;
  pageCount?: number;
  paradigm?: string;
  browser?: any;
  type?: string;
  scriptCount?: number;
  isSelected?: boolean;
  isDefault?: boolean;
  isExpand?: boolean;
  mobilePlatform?: string;
  mobileAppType?: string;
  page?: any;
  mobileFilePath?: string;
  mobileFileName?: string;
  mappingName?: string;
  mappingId?: string;
}

export interface SortOption {
  id: string;
  label: string;
  direction?: 'asc' | 'desc';
}

export interface PaginationInfo {
  currentStart: number;
  currentEnd: number;
  total: number;
  label: string;
}

export interface PaginationConfig {
  currentPage: number;
  pageSize: number;
  total: number;
}
@Component({
  selector: 'app-accordion-table-view',
  standalone: true,
  imports: [CommonModule, FormsModule, NzPaginationModule, ZorroTableComponent, MatTooltipModule, MenuDropdownComponent],
  templateUrl: './accordion-table-view.component.html',
  styleUrl: './accordion-table-view.component.scss'
})
export class AccordionTableViewComponent implements OnInit, OnChanges {
  @Input() sections: AccordionSection[] = [];
  @Input() typeId?: string;
  @Input() itemLabel: string = 'Test Cases';
  @Input() paginationInfo?: any;
  @Input() sortOptions: any[] = [];
  @Input() singleSectionSelection: boolean = false;
  @Input() showHeader: boolean = true;
  @Input() showTestPlanHeader: boolean = false;
  @Input() contentTemplate?: TemplateRef<any>;
  @Input() showPagination: boolean = true;
  @Input() autoExpandFirst: boolean = false; // Auto-expand first item on init
  @Input() showSectionCheckbox: boolean = false; // Show checkbox in accordion header
  @Input() showSaveButton: boolean = false;           // Show Save button in test-plan-header toolbar
  @Input() isSaveLoading: boolean = false;            // Shows a spinner on the Save button while the API call is in progress
  @Input() selectAllSectionsOnly: boolean = false;    // true = Select All toggles section checkboxes only; false = also selects table rows inside
  @Input() showSelectAll: boolean = true;             // false = show pagination info on the left instead of Select All checkbox
  @Input() info: any = {
    display: false,
    tooltip: '',
  };
  @Input() hideTcCount: boolean = false; // Hide the test case count in the header
  @Output() sortChange = new EventEmitter<any>();
  @Output() sectionExpand = new EventEmitter<{ section: AccordionSection, typeId?: string }>();
  @Output() tableRowSelect = new EventEmitter<{ section: AccordionSection, selectedRows: any[] }>();
  @Output() tableActions = new EventEmitter<{ section: AccordionSection, action: any }>();
  @Output() sectionCheckboxChange = new EventEmitter<{ section: AccordionSection, checked: boolean }>();
  @Output() selectAllChange = new EventEmitter<boolean>();
  @Output() switchToggle = new EventEmitter<boolean>();
  @Output() dropdown1Change = new EventEmitter<any>();
  @Output() dropdown2Change = new EventEmitter<any>();
  @Output() infoIconClick = new EventEmitter<any>();
  @Output() saveClick = new EventEmitter<{ sections: AccordionSection[]; isDefault: boolean; engine: any; browser: any; typeId?: string }>();

  selectedSort: any = null;
  isSelectAllChecked: boolean = false;
  isSwitchEnabled: boolean = false;
  dropdown1Value: any = null;
  dropdown2Value: any = null;
  dropdown2Open: boolean = false;
  @Input() dropdown1Options: any[] = [];
  @Input() dropdown2Options: any[] = [];
  @Input() dropdown1Placeholder: string = 'Execution Engine/Device';
  @Input() dropdown2Placeholder: string = 'Browser';
  @Input() dropdown1Config: any = null;

  @Input() tableOptions: any = {};
  /**
   * When set, selecting a value from the toolbar's first dropdown will
   * automatically write that value to `row[dropdown1RowKey]` for every row
   * in every section — keeping per-row dropdowns in sync with the global default.
   */
  @Input() dropdown1RowKey: string = '';
  /**
   * When set, selecting a value from the toolbar's second dropdown (browser) will
   * automatically write that value to `row[dropdown2RowKey]` for every row
   * in every section — keeping per-row browser dropdowns in sync with the global default.
   */
  @Input() dropdown2RowKey: string = '';
  private internalSections: AccordionSection[] = [];
  defaultTableOptions: any = {
    pageSize: 10000,
    showPagination: false,
    showPageSizeChanger: false,
    showSelection: true,
    expandable: true
  };

  /** Configurable page size – defaults to 10 if not supplied by the parent. */
  @Input() pageSize: number = 10;

  paginationConfig: any = {
    currentPage: 1,
    pageSize: 10,
    total: 0
  };

  ngOnInit(): void {
    this.defaultTableOptions = {...this.defaultTableOptions, ...this.tableOptions};
    if (this.sortOptions.length > 0) {
      this.selectedSort = this.sortOptions[0];
    }
    this.internalSections = [...this.sections];
    // Sync configurable page size
    this.paginationConfig.pageSize = this.pageSize;
    // Initialize isSelected property for sections if showSectionCheckbox is true
    if (this.showSectionCheckbox) {
      this.internalSections.forEach(section => {
        if (section.isSelected === undefined) {
          section.isSelected = false;
        }
      });
    }
    // Set total count for pagination
    if (this.paginationConfig) {
      this.paginationConfig.total = this.internalSections.length;
    }

    // Auto-expand first section if enabled
    if (this.autoExpandFirst && this.internalSections.length > 0) {
      const firstSection = this.internalSections[0];
      firstSection.expanded = true;
      // Emit expand event to trigger data loading
      this.sectionExpand.emit({ section: firstSection, typeId: this.typeId });
    }

    // Auto-expand sections that have isExpand:true from BE
    this.internalSections.forEach(section => {
      if (section.isExpand && !section.expanded) {
        section.expanded = true;
        this.sectionExpand.emit({ section, typeId: this.typeId });
      }
    });

    // Default dropdown2 to first option if available and no value set
    if (this.dropdown2Options.length > 0 && this.dropdown2Value == null) {
      this.dropdown2Value = this.dropdown2Options[0].value;
      this.dropdown2Change.emit(this.dropdown2Value);
    }
  }

  onSectionCheckboxChange(section: AccordionSection): void {
    // Emit the change event with current state
    this.sectionCheckboxChange.emit({ section, checked: section.isSelected || false });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sections'] && !changes['sections'].firstChange) {
      this.internalSections = [...this.sections];
      // Re-sync page size in case it arrived after sections
      this.paginationConfig.pageSize = this.pageSize;
      if (this.selectedSort) {
        this.applySorting();
      }
      if (this.paginationConfig) {
        this.paginationConfig.total = this.internalSections.length;
      }

      // Auto-expand first section if enabled and sections were updated
      if (this.autoExpandFirst && this.internalSections.length > 0) {
        const firstSection = this.internalSections[0];
        if (!firstSection.expanded && !firstSection.loading && !firstSection.tableRows) {
          firstSection.expanded = true;
          this.sectionExpand.emit({ section: firstSection, typeId: this.typeId });
        }
      }

      // Auto-expand sections that have isExpand:true from BE
      this.internalSections.forEach(section => {
        if (section.isExpand && !section.expanded && !section.tableRows) {
          section.expanded = true;
          this.sectionExpand.emit({ section, typeId: this.typeId });
        }
      });
    }
  }

  get paginatedSections(): AccordionSection[] {
    if (!this.showPagination || !this.paginationConfig) {
      return this.internalSections;
    }
    const startIndex = (this.paginationConfig.currentPage - 1) * this.paginationConfig.pageSize;
    const endIndex = startIndex + this.paginationConfig.pageSize;
    return this.internalSections.slice(startIndex, endIndex);
  }

  get currentStart(): number {
    if (!this.paginationConfig || this.paginationConfig.total === 0) return 0;
    return (this.paginationConfig.currentPage - 1) * this.paginationConfig.pageSize + 1;
  }

  get currentEnd(): number {
    if (!this.paginationConfig) return 0;
    const end = this.paginationConfig.currentPage * this.paginationConfig.pageSize;
    return Math.min(end, this.paginationConfig.total);
  }

  get totalItems(): number {
    return this.paginationConfig?.total || 0;
  }

  get selectedSection(): AccordionSection | null {
    if (!this.showSectionCheckbox) return null;
    return this.internalSections.find(section => section.isSelected) || null;
  }

  get hasAnySelected(): boolean {
    return this.internalSections.some(section => section.isSelected);
  }

  toggleSection(section: AccordionSection): void {
    // When single-section mode has an active selection, only that section can expand/collapse.
    if (this.singleSectionSelection && this.hasAnySelected && !section.isSelected) {
      return;
    }
    const wasExpanded = section.expanded;
    section.expanded = !section.expanded;

    // Emit event when section is being expanded and doesn't have data yet
    if (!wasExpanded && section.expanded) {
      this.sectionExpand.emit({ section, typeId: this.typeId });
    }
  }

  onSelectAllChange(checked: boolean): void {
    this.isSelectAllChecked = checked;

    if (this.selectAllSectionsOnly) {
      // Only toggle the section-level checkboxes; do NOT touch table rows inside sections
      this.internalSections.forEach(section => {
        section.isSelected = checked;
      });
    } else {
      // Toggle table rows inside every section (original behaviour)
      this.internalSections.forEach(section => {
        if (section.tableRows) {
          section.tableRows.forEach((row: any) => (row.select = checked));
          section.tableRows = [...section.tableRows];
        }
        if (!section.expanded) {
          section.expanded = true;
          this.sectionExpand.emit({ section, typeId: this.typeId });
        }
      });
    }

    this.selectAllChange.emit(this.isSelectAllChecked);
  }

  onSwitchToggle(): void {
    this.switchToggle.emit(this.isSwitchEnabled);
  }

  onSaveClick(): void {
    this.saveClick.emit({
      sections: this.internalSections,
      isDefault: this.isSwitchEnabled,
      engine: this.dropdown1Value,
      browser: this.dropdown2Value,
      typeId: this.typeId,
    });
  }

  onDropdown1Change(event?: any): void {
    // Handle event from menu-dropdown which may have value, id, agentType, etc.
    if (event) {
      this.dropdown1Value = event?.value || event?.id || event;
    }
    this.dropdown1Change.emit(event || this.dropdown1Value);

    // Propagate the selected global engine/device to every row so per-row dropdowns stay in sync.
    if (this.dropdown1RowKey) {
      this.internalSections.forEach(section => {
        if (section.tableRows?.length) {
          section.tableRows.forEach((row: any) => {
            // Update the row with the selected value
            row[this.dropdown1RowKey] = this.dropdown1Value;

            // Also update common execution engine/device related fields
            if (event?.agentType) {
              // It's an execution engine
              row.agentId = this.dropdown1Value;
              row.agentType = event.agentType;
              row.executionType = 'WEB';
              delete row.deviceId;
              delete row.executionType;
            } else if (event?.deviceType || event?.platform) {
              // It's a device
              row.deviceId = this.dropdown1Value;
              row.agentType = undefined;
              row.executionType = 'MOBILE';
            }
          });
          // Create a new array reference to trigger Angular change detection.
          section.tableRows = [...section.tableRows];
        }
      });
    }
  }

  onDropdown2Change(): void {
    this.dropdown2Change.emit(this.dropdown2Value);
  }

  toggleDropdown2(event: Event): void {
    event.stopPropagation();
    if (!this.isSwitchEnabled) return;
    this.dropdown2Open = !this.dropdown2Open;
  }

  onDropdown2Select(event: Event, value: string): void {
    event.stopPropagation();
    this.dropdown2Value = value;
    this.dropdown2Open = false;
    this.dropdown2Change.emit(this.dropdown2Value);
    // Propagate the selected global browser to every row so per-row browser dropdowns stay in sync.
    if (this.dropdown2RowKey) {
      this.internalSections.forEach(section => {
        if (section.tableRows?.length) {
          section.tableRows.forEach((row: any) => {
            row[this.dropdown2RowKey] = this.dropdown2Value;
          });
          // New array reference to trigger Angular change detection.
          section.tableRows = [...section.tableRows];
        }
      });
    }
  }

  @HostListener('document:click')
  closeDropdown2(): void {
    this.dropdown2Open = false;
  }

  selectSort(option: any): void {
    this.selectedSort = option;
    this.sortChange.emit(option);
  }

  toggleSortDirection(option: any): void {
    console.log('SORTTTT', option);
    if (this.selectedSort?.id === option.id) {
      const currentDirection = this.selectedSort.direction || 'asc';
      const newDirection = currentDirection === 'asc' ? 'desc' : 'asc';
      this.selectedSort = { ...option, direction: newDirection };
    } else {
      this.selectedSort = { ...option, direction: option.direction || 'asc' };
    }

    // Apply sorting logic
    this.applySorting();
    this.sortChange.emit(this.selectedSort);
  }

  private applySorting(): void {
    if (!this.selectedSort) return;

    if (this.selectedSort.id === 'test-case-count') {
      this.internalSections = [...this.internalSections].sort((a, b) => {
        return this.selectedSort.direction === 'asc'
          ? a.testcaseCount - b.testcaseCount
          : b.testcaseCount - a.testcaseCount;
      });
    }
    // Reset to first page after sorting
    if (this.paginationConfig) {
      this.paginationConfig.currentPage = 1;
      this.paginationConfig.total = this.internalSections.length;
    }
  }

  onTableRowSelect(event: any, section: AccordionSection): void {
    this.tableRowSelect.emit({ section: { ...section, typeId: this.typeId }, selectedRows: event });
  }

  getBrowserImage(value: string): string {
    return this.dropdown2Options.find(o => o.value === value)?.image || '';
  }

  handleTableActions(event: any, section: AccordionSection): void {
    if (event?.value?.deviceType) {
      const rowId = event?.data?.row?.id;

      const targetRow = section.tableRows?.find(
        (row: any) => row.id === rowId
      );

      if (targetRow) {
        targetRow.deviceId = event.value.value;
        targetRow.executionType = 'MOBILE';

        // Optional: remove agentId if mobile execution
        delete targetRow.agentId;
      }
    }
    this.tableActions.emit({ section: { ...section, typeId: this.typeId }, action: event });
  }

  onPageIndexChange(pageIndex: number): void {
    if (!this.paginationConfig) return;
    this.paginationConfig.currentPage = pageIndex;
  }

  onPageSizeChange(pageSize: number): void {
    if (!this.paginationConfig) return;
    this.paginationConfig.pageSize = pageSize;
    this.paginationConfig.currentPage = 1;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    });
  }
}
